import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash-es';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlLocalAcctDescService } from './gl-local-acct-desc.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-gl-local-acct-desc',
    templateUrl: './gl-local-acct-desc.component.html',
    styleUrls: ['./gl-local-acct-desc.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GlLocalAcctDescComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {};
  rowData: any = {};

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  localDescForm: UntypedFormGroup;
  crudLoading: boolean = false;
  minimise: boolean = false;
  activeTab: number = 0;

  chartacct_data: any = {
    localacct: '',
    localacctdesc: '',
    localacctdesc_new: ''
  };

  auditData: any[] = [];
  viewColumnNames: string[] = ["Updated On", "Chart", "Local Account Description", "DML Type", "Old New Flag", "Updated By"];
  columnNames: string[] = [];
  noRecords: boolean = false;
  sort_key: any = {};
  column: string = '';
  reverse: boolean = false;
  logged_in_user: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactoryService: JsonObjectFactoryService,
    private glLocalAcctDescService: GlLocalAcctDescService
  ) {
    this.localDescForm = this.fb.group({
      localAcountDescInput: ['', [Validators.required, Validators.maxLength(100)]]
    });

    for (let i = 1; i < 7; i++) {
      this.sort_key[i] = 0;
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.rowData && Object.keys(this.rowData).length > 0 &&
        this.resolveApiBase()) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    // Initialize form data from rowData
    if (this.rowData) {
      this.chartacct_data = {
        localacct: this.rowData.LOCAL_ACCT || '',
        localacctdesc: this.rowData.LOCAL_ACCT_DESC || '',
        localacctdesc_new: this.rowData.LOCAL_ACCT_DESC || ''
      };

      // Set form value
      this.localDescForm.patchValue({
        localAcountDescInput: this.chartacct_data.localacctdesc_new
      });
    }
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  // Tab management
  onTabChange(tabIndex: number): void {
    this.activeTab = tabIndex;
    if (tabIndex === 1) {
      // Audit tab clicked
      this.getAuditSummary();
    }
  }

  // Audit functionality
  getAuditSummary(): void {
    if (!this.rowData?.LOCAL_ACCT_KEY) {
      return;
    }

    const params = {
      "action_code": 'smi2fr',
      "sso_id": this.logged_in_user,
      "local_acct_key": this.rowData.LOCAL_ACCT_KEY
    };

    this.jsonObjectFactoryService.getJSONObj(
      this.resolveApiBase() + '/loadJsonObject',
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.auditData = data.jsonObject || [];

        // Checking to see if any data was returned
        if (this.auditData && this.auditData.length > 0) {
          this.columnNames = Object.keys(this.auditData[0]);
          this.noRecords = false;
        } else {
          this.noRecords = true;
        }
      },
      (error: any) => {
        console.error('Error loading audit data:', error);
        this.gtwSnackbarService.openSnackBar('Error loading audit data. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  // Get sorted audit data
  getSortedAuditData(): any[] {
    if (!this.column || !this.auditData || this.auditData.length === 0) {
      return this.auditData;
    }

    const sorted = [...this.auditData].sort((a: any, b: any) => {
      const aVal = a[this.column];
      const bVal = b[this.column];

      if (aVal === bVal) return 0;

      if (this.reverse) {
        return aVal > bVal ? -1 : 1;
      } else {
        return aVal < bVal ? -1 : 1;
      }
    });

    return sorted;
  }

  // Function to handle sorting columns in Audit tab
  sortBy(colNum: number, sortKey: number): void {
    this.column = this.columnNames[colNum - 1];
    if (sortKey === 0 || sortKey === 2) {
      this.sort_key[colNum] = 1;
      this.reverse = false;
    } else if (sortKey === 1) {
      this.sort_key[colNum] = 2;
      this.reverse = true;
    }

    // When one column is chosen to be sorted, we set the other columns to be labeled as
    // "neutral," that is, they are not being sorted on
    for (let i = 1; i < 7; i++) {
      if (i === Number(colNum)) {
        continue;
      } else {
        this.sort_key[i] = 0;
      }
    }
  }

  // Form validation helper
  get localAcountDescInput() {
    return this.localDescForm.get('localAcountDescInput');
  }

  // Input validation - no special characters
  onInputChange(event: any): void {
    let value = event.target.value;
    if (value) {
      // Remove special characters except word characters, spaces, hyphens, forward slashes, colons, periods, question marks
      let cleanValue = value.replace(/[^\w\s-/:.?]/gi, '');
      if (cleanValue !== value) {
        this.localDescForm.patchValue({
          localAcountDescInput: cleanValue
        });
        this.chartacct_data.localacctdesc_new = cleanValue;
      } else {
        this.chartacct_data.localacctdesc_new = value;
      }
    }
  }

  reset(): void {
    // Reset form to original values
    this.chartacct_data = {
      localacct: this.rowData.LOCAL_ACCT || '',
      localacctdesc: this.rowData.LOCAL_ACCT_DESC || '',
      localacctdesc_new: this.rowData.LOCAL_ACCT_DESC || ''
    };

    this.localDescForm.patchValue({
      localAcountDescInput: this.chartacct_data.localacctdesc_new
    });
    this.localDescForm.markAsPristine();
    this.crudLoading = false;
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    if (this.localDescForm.invalid) {
      this.gtwSnackbarService.openSnackBar('Please correct the form errors', 'gtw-snackbar--red');
      return;
    }

    this.crudLoading = true;

    const localAcountDescDetails: any[] = [];
    const returnObj: any = {};
    returnObj['type'] = 'E';
    returnObj["object_id"] = this.rowData.object_id;
    returnObj['local_acct_key'] = this.rowData.LOCAL_ACCT_KEY;
    returnObj['old_local_acct_desc'] = this.rowData.LOCAL_ACCT_DESC;
    returnObj["new_local_acct_desc"] = this.chartacct_data.localacctdesc_new;
    returnObj['group_object_key'] = this.rowData.GROUP_OBJ_KEY;
    returnObj['tax_year'] = this.rowData.TAX_YEAR;
    returnObj['source_system'] = this.rowData.SOURCE_SYSTEM;
    localAcountDescDetails.push(returnObj);

    const message = "Local Acct Desc has been successfully save/updated";

    this.sendDetails(returnObj, localAcountDescDetails, message);
  }

  sendDetails(returnObj: any, localacctdes: any[], message: string): void {
    // Get data source for params
    const globalParams = this.globalService || {};

    // Build params with all required fields matching the specified format
    const params: any = {
      // jsonSettings and jsonObj
      jsonSettings: JSON.stringify(returnObj || {}),
      jsonObj: JSON.stringify(localacctdes || []),

      // Standard params
      tax_year: this.rowData?.TAX_YEAR || this.filterParams?.tax_year || globalParams?.tax_year || '',
      screen_key: this.filterParams?.screen_key || globalParams?.screen_key || this.rowData?.SCREEN_KEY || '155',
      scenario: this.filterParams?.scenario || globalParams?.scenario || this.rowData?.SCENARIO || '74',
      jcd_key: this.filterParams?.jcd_key || globalParams?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || globalParams?.jcd_ta_key || this.rowData?.JCD_TA_KEY || '1113',
      activity_key: this.filterParams?.activity_key || globalParams?.activity_key || this.rowData?.ACTIVITY_KEY || '384',
      screen_type: this.filterParams?.screen_type || globalParams?.screen_type || this.rowData?.SCREEN_TYPE || 'grid',
      is_locked: this.filterParams?.is_locked || globalParams?.is_locked || this.rowData?.IS_LOCKED || 'N',
      issue_id: this.filterParams?.issue_id || globalParams?.issue_id || this.rowData?.ISSUE_ID || '',
      issue_short_desc: this.filterParams?.issue_short_desc || globalParams?.issue_short_desc || this.rowData?.ISSUE_SHORT_DESC || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || globalParams?.issue_id_for_tx_save || this.rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      scenarioCode: this.filterParams?.scenarioCode || globalParams?.scenarioCode || this.rowData?.SCENARIO_CODE || 'RAF',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || this.rowData?.IS_ISSUE_KEY_ENABLED || 'N',
      checkLockedbj: JSON.stringify([{ scenario_key: this.filterParams?.scenario || globalParams?.scenario || this.rowData?.SCENARIO || '74' }]),
      entity_type: this.filterParams?.entity_type || globalParams?.entity_type || this.rowData?.ENTITY_TYPE || 'REG',
      chart_filter: this.rowData?.LOCAL_CHART_KEY || this.filterParams?.chart_filter || globalParams?.chart_filter || '',
      process_name: 'LOCAL_DESC_SAVE'
    };

    // 🚀 CRITICAL: Pass filterParams and rowData to service
    this.glLocalAcctDescService.saveLocalDesc(
      this.resolveApiBase(), // Use base_url (contains /gtw/)
      returnObj,
      localacctdes,
      this.globalService,
      this.filterParams,  // 🚀 Pass dynamic filter params
      this.rowData,       // 🚀 Pass row data
      params              // 🚀 Pass custom params structure
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');

          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  cancel(): void {
    // Following pattern - dispatch CustomEvent to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });

    if (document.querySelector('gtw-gl-local-acct-desc-ce')) {
      document.querySelector('gtw-gl-local-acct-desc-ce')?.dispatchEvent(updateEvent);
    }
  }
}

